import {NavTabs} from "@/components/navTabs/NavTabs.tsx";
import {Helmet} from "react-helmet-async";

import {StatusPageLayoutPreset, StatusPageTheme, type StatusPage} from "@/lib/types/statuspages.ts";
import {CurrentStatusOverview} from "../../components/currentstatus/CurrentStatusOverview.tsx";
import {HistoryOverview} from "../../components/history/HistoryOverview.tsx";
import {StatusPageFooter} from "../../components/StatusPageFooter.tsx";
import styles from "../../StatusPageConsumption.module.scss";
import {CurrentStatusTabType, StatusPageTab} from "../../types.ts";
import {getCrossTabReachability} from "../../utils/crossTabLinks.ts";
import type {ThemeProps} from "../types.ts";
import {InspireHeader} from "./InspireHeader.tsx";
import inspireStyles from "./InspireTheme.module.scss";

const TAB_ITEMS = [
  {id: StatusPageTab.STATUS, title: "Current Status"},
  {id: StatusPageTab.HISTORY, title: "History"},
];

export const InspireTheme = ({
  statusPage,
  slug,
  mode,
  pageId,
  isDark,
  onToggleDarkMode,
  activeTab,
  onTabChange,
  currentStatusTab,
  onStatusTabChange,
  historyTab,
  onHistoryTabChange,
  historyMounted,
  historyResult,
  allIncidents,
  statusDateRange,
  onRefresh,
  autoRefreshPageKey,
  componentStatuses,
  metrics,
  componentLinkTo,
}: ThemeProps) => {
  // Custom header/footer HTML and scoped CSS mirror the legacy Django INSPIRE theme.
  // All three values are sanitized server-side on save (validate_and_sanitize_html /
  // validate_and_sanitize_css), so injecting them as raw markup here is safe.
  const customHeaderHtml = statusPage.custom_header_html_inspire?.trim();
  const customFooterHtml = statusPage.custom_footer_html_inspire?.trim();
  const customCss = statusPage.custom_css_inspire?.trim();

  const {canViewActiveIncidents, canViewActiveMaintenance, canViewPastIncidents, canViewPastMaintenance} =
    getCrossTabReachability(statusPage, historyResult);

  const showStatus = statusPage.show_status_tab !== false;

  return (
    <div
      // theme-inspire + dark/light mirror the legacy body classes so saved custom CSS keeps matching.
      className={`${styles.pageWrapper} ${inspireStyles.pageWrapper} theme-inspire ${isDark ? "dark" : "light"}`}
      data-theme={isDark ? "dark" : "light"}
    >
      <Helmet>
        <title>{statusPage.name} — Status</title>
        {statusPage.favicon_url && <link rel="icon" href={statusPage.favicon_url} />}
      </Helmet>

      {customCss && (
        <style
          dangerouslySetInnerHTML={{__html: `#inspire-custom-header, #inspire-custom-footer {\n${customCss}\n}`}}
        />
      )}

      <InspireHeader
        statusPage={statusPage}
        allIncidents={allIncidents}
        slug={slug}
        isDark={isDark}
        onToggleDarkMode={onToggleDarkMode}
        onRefresh={onRefresh}
        autoRefreshPageKey={autoRefreshPageKey}
        customHeaderHtml={customHeaderHtml}
        showOverallStatus={statusPage.show_overall_status}
      />

      <main className={inspireStyles.body}>
        {(() => {
          const visibleTabs = TAB_ITEMS.filter((t) =>
            t.id === StatusPageTab.STATUS ? showStatus : statusPage.show_history_tab
          );
          return visibleTabs.length > 1 ? (
            <div className={inspireStyles.tabBar}>
              <NavTabs
                items={visibleTabs}
                selectedId={activeTab}
                selectTab={(tab) => onTabChange(tab as StatusPageTab)}
                variant="statuspageContent"
              />
            </div>
          ) : null;
        })()}
        {showStatus && (
          <div
            className={`${inspireStyles.contentFrame}${activeTab !== StatusPageTab.STATUS ? ` ${styles.hidden}` : ""}`}
          >
            <div className={inspireStyles.tabContentCard}>
              <CurrentStatusOverview
                statusPage={statusPage as unknown as StatusPage}
                componentStatuses={componentStatuses}
                activeTab={currentStatusTab}
                onTabChange={(tab) => onStatusTabChange(tab)}
                onSwitchToHistory={(tab) => {
                  if (!statusPage.show_history_tab) return;
                  onTabChange(StatusPageTab.HISTORY);
                  onHistoryTabChange(tab);
                }}
                canViewPastIncidents={canViewPastIncidents}
                canViewPastMaintenance={canViewPastMaintenance}
                metrics={metrics}
                mode={mode}
                slug={slug}
                pageId={pageId}
                variant="inspire"
                componentLinkTo={componentLinkTo}
                layout={statusPage.layout_preset as StatusPageLayoutPreset}
                statusBarProps={
                  statusPage.show_history_snake && statusDateRange
                    ? {
                        incidents: allIncidents,
                        downtimeHistory: historyResult?.downtime_history ?? [],
                        statusMetadata: statusPage.component_status_metadata,
                        uptimeCalculationType: statusPage.uptime_calculation_type,
                        defaultStatusDateRange: statusPage.default_status_date_range || 7,
                        timezone: statusPage.used_timezone,
                        showHistorySnake: statusPage.show_history_snake,
                        globalIsOperational: statusPage.global_is_operational,
                        hasMaintenanceState: statusPage.has_components_under_maintenance_state,
                        components: statusPage.components,
                        start: statusDateRange.start,
                        end: statusDateRange.end,
                        barOnly: true,
                        onViewIncidents:
                          statusPage.show_status_tab !== false
                            ? () => onStatusTabChange(CurrentStatusTabType.ACTIVE_INCIDENTS)
                            : undefined,
                      }
                    : null
                }
              />
            </div>
          </div>
        )}
        {statusPage.show_history_tab && (
          <div
            className={`${inspireStyles.contentFrame}${activeTab !== StatusPageTab.HISTORY ? ` ${styles.hidden}` : ""}`}
          >
            {historyMounted && (
              <div className={inspireStyles.tabContentCard}>
                <HistoryOverview
                  activeTab={historyTab}
                  onTabChange={(tab) => onHistoryTabChange(tab)}
                  mode={mode}
                  variant={StatusPageTheme.INSPIRE}
                  layout={statusPage.layout_preset as StatusPageLayoutPreset}
                  onSwitchToCurrentStatus={(tab) => {
                    if (statusPage.show_status_tab === false) return;
                    onTabChange(StatusPageTab.STATUS);
                    onStatusTabChange(tab);
                  }}
                  canViewActiveIncidents={canViewActiveIncidents}
                  canViewActiveMaintenance={canViewActiveMaintenance}
                  componentLinkTo={componentLinkTo}
                />
              </div>
            )}
          </div>
        )}
      </main>
      {statusPage.show_branding_footer && <StatusPageFooter className={inspireStyles.footer} />}

      {customFooterHtml && <footer id="inspire-custom-footer" dangerouslySetInnerHTML={{__html: customFooterHtml}} />}
    </div>
  );
};
